// src/pages/CategoriesPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getCategoriesAdmin,
  createCategory,
  updateCategory,
  deleteCategory,
  reorderCategories,
  setCategoryVisibility,
} from "../services/categoryService";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ===== ENV =====
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
const CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_CLOUD ||
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
  "";

// ===== Helpers =====
function resolveImageSrc({ image, imageUrl }) {
  if (typeof imageUrl === "string" && imageUrl.startsWith("http")) return imageUrl;
  if (typeof image === "string" && image.startsWith("http")) return image;
  if (
    CLOUD_NAME &&
    typeof image === "string" &&
    image.trim() &&
    !image.includes("..") &&
    !image.includes("uploads/")
  ) {
    const publicId = image.replace(/^\/+/, "");
    return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/q_auto,f_auto/${publicId}`;
  }
  if (typeof image === "string" && image.trim()) return `${API_BASE}/uploads/${image}`;
  return null;
}

function flattenCategories(cats, prefix = "") {
  return cats.flatMap((cat) => [
    { id: cat.id, name: `${prefix}${cat.name}`, parentId: cat.parentId },
    ...(cat.subcategories
      ? flattenCategories(cat.subcategories, prefix + "--")
      : []),
  ]);
}

function mapIdToParentId(tree) {
  const map = new Map();
  const walk = (nodes, parentId = null) => {
    for (const n of nodes) {
      map.set(n.id, parentId);
      if (Array.isArray(n.subcategories) && n.subcategories.length) {
        walk(n.subcategories, n.id);
      }
    }
  };
  walk(tree, null);
  return map;
}

function siblingsOf(tree, parentId) {
  if (parentId == null) return tree;
  const stack = [...tree];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    if (node.id === parentId) return node.subcategories || [];
    if (Array.isArray(node.subcategories)) stack.push(...node.subcategories);
  }
  return [];
}

function replaceSiblings(tree, parentId, nextSiblings) {
  const clone = (n) => ({
    ...n,
    subcategories: Array.isArray(n.subcategories) ? n.subcategories.map(clone) : [],
  });

  if (parentId == null) return nextSiblings.map(clone);

  const deep = tree.map(clone);
  const dfs = (nodes) => {
    for (const n of nodes) {
      if (n.id === parentId) {
        n.subcategories = nextSiblings.map(clone);
        return true;
      }
      if (Array.isArray(n.subcategories) && dfs(n.subcategories)) return true;
    }
    return false;
  };
  dfs(deep);
  return deep;
}

/** Оновити isHidden в дереві по id (immutably) */
function updateHiddenInTree(nodes, id, isHidden) {
  return nodes.map((n) => {
    if (n.id === id) {
      return {
        ...n,
        isHidden,
        subcategories: Array.isArray(n.subcategories) ? n.subcategories : [],
      };
    }
    if (Array.isArray(n.subcategories) && n.subcategories.length) {
      return { ...n, subcategories: updateHiddenInTree(n.subcategories, id, isHidden) };
    }
    return n;
  });
}

// ===== Sortable Row =====
function SortableRow({
  cat,
  level,
  onEdit,
  onDelete,
  onToggleVisibility,
  collapsed,
  onToggleCollapse,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "#FFF8E1" : undefined,
    opacity: cat.isHidden ? 0.72 : 1,
  };

  const src = resolveImageSrc({ image: cat.image, imageUrl: cat.imageUrl });

  return (
    <tr ref={setNodeRef} style={style} className="border-b hover:bg-[#F5F0E6] transition">
      <td className="p-3 w-[70px]">
        {src ? (
          <img
            src={src}
            alt={cat.name}
            className="h-16 w-16 object-cover rounded shadow-sm"
          />
        ) : (
          <div className="h-16 w-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
            No
          </div>
        )}
      </td>

      <td className="p-3 font-semibold text-[#4B2E2B]">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700 px-2"
            title="Перетягнути"
          >
            ☰
          </button>

          <span style={{ paddingLeft: `${level * 16}px` }}>
            {level > 0 && <span className="mr-1">↳</span>}
            {cat.name}

            {/* бейдж Hidden */}
            {cat.isHidden ? (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 border">
                Hidden
              </span>
            ) : (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                Active
              </span>
            )}

            {Array.isArray(cat.subcategories) && cat.subcategories.length > 0 && (
              <button
                type="button"
                onClick={() => onToggleCollapse(cat.id)}
                className="ml-2 text-gray-500 hover:text-gray-700"
                title={collapsed[cat.id] ? "Розгорнути" : "Згорнути"}
              >
                {collapsed[cat.id] ? "▶" : "▼"}
              </button>
            )}
          </span>
        </div>

        {cat.name_en ? (
          <div className="text-xs text-gray-500 ml-10" style={{ paddingLeft: `${level * 16}px` }}>
            EN: {cat.name_en}
          </div>
        ) : null}
      </td>

      <td className="p-3 text-gray-600 italic">
        {cat.parentId ? "Підкатегорія" : "Головна"}
      </td>

      <td className="p-3">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => onEdit(cat)}
            className="bg-[#A47551] text-white px-3 py-1 rounded hover:bg-[#8B5E3C]"
          >
            Edit
          </button>

          <button
            onClick={() => onToggleVisibility(cat)}
            className={`px-3 py-1 rounded text-white ${
              cat.isHidden ? "bg-emerald-600 hover:bg-emerald-700" : "bg-gray-700 hover:bg-gray-800"
            }`}
            title={cat.isHidden ? "Показати категорію" : "Приховати категорію"}
          >
            {cat.isHidden ? "Show" : "Hide"}
          </button>

          <button
            onClick={() => onDelete(cat.id)}
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

// ===== Page =====
export default function CategoriesPage() {
  const [tree, setTree] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [collapsed, setCollapsed] = useState({});

  const [form, setForm] = useState({ name: "", name_en: "", parentId: "" });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [editingImageUrl, setEditingImageUrl] = useState(null);

  const idToParentId = useMemo(() => mapIdToParentId(tree), [tree]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const fileInputRef = useRef(null);

  // ===== Load (ADMIN) =====
  const load = async () => {
    const data = await getCategoriesAdmin(); // ✅ includeHidden=1
    setTree(data);

    const collapsedMap = {};
    data.forEach((cat) => {
      if (!cat.parentId && cat.subcategories?.length) collapsedMap[cat.id] = true;
    });
    setCollapsed(collapsedMap);
    setFlatCategories(flattenCategories(data));
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const handleToggleCollapse = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));
  const onFormChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onPickImage = (e) => setImage(e.target.files?.[0] || null);
  useEffect(() => {
    if (!image) return setImagePreview(null);
    const url = URL.createObjectURL(image);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const clearFile = () => fileInputRef.current && (fileInputRef.current.value = "");
  const resetForm = () => {
    setForm({ name: "", name_en: "", parentId: "" });
    setImage(null);
    setImagePreview(null);
    setEditingId(null);
    setEditingImage(null);
    setEditingImageUrl(null);
    clearFile();
  };

  // ===== CRUD =====
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      if (form.name_en) fd.append("name_en", form.name_en);
      fd.append("parentId", form.parentId || "");
      if (image) fd.append("image", image);

      if (editingId) await updateCategory(editingId, fd);
      else await createCategory(fd);

      resetForm();
      await load();
    } catch (err) {
      console.error("Помилка збереження категорії:", err);
      alert("Не вдалося зберегти категорію");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Видалити категорію?")) return;
    try {
      await deleteCategory(id);
      await load();
    } catch (err) {
      console.error("Помилка видалення категорії:", err);
      alert("Не вдалося видалити категорію");
    }
  };

  const onEdit = (cat) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name || "",
      name_en: cat.name_en || "",
      parentId: cat.parentId || "",
    });
    setEditingImage(cat.image || null);
    setEditingImageUrl(cat.imageUrl || null);
    setImage(null);
    setImagePreview(null);
    clearFile();
  };

  const onCancelEdit = () => resetForm();

  const applyLocalReorder = async (parentId, newOrderIds) => {
    const sibs = siblingsOf(tree, parentId);
    const next = newOrderIds.map((id) => sibs.find((s) => s.id === id)).filter(Boolean);
    setTree((prev) => replaceSiblings(prev, parentId, next));
    try {
      await reorderCategories(parentId ?? null, newOrderIds);
    } catch (e) {
      console.error("Помилка збереження порядку:", e);
      await load();
    }
  };

  // ✅ Hide/Show category
  const onToggleVisibility = async (cat) => {
    const nextHidden = !cat.isHidden;
    const label = nextHidden ? "приховати" : "показати";
    if (!window.confirm(`Підтвердити: ${label} категорію?`)) return;

    // optimistic
    setTree((prev) => updateHiddenInTree(prev, cat.id, nextHidden));

    try {
      const updated = await setCategoryVisibility(cat.id, nextHidden);

      // якщо бекенд повернув актуальну категорію — підставимо isHidden (на всяк)
      if (updated && typeof updated.isHidden === "boolean") {
        setTree((prev) => updateHiddenInTree(prev, cat.id, updated.isHidden));
      }
    } catch (err) {
      console.error("Помилка зміни видимості категорії:", err);

      // rollback
      setTree((prev) => updateHiddenInTree(prev, cat.id, !nextHidden));
      alert("Не вдалося змінити видимість категорії");
    }
  };

  // ===== Drag & Drop =====
  const renderGroup = (nodes, parentId = null, level = 0) => {
    const ids = nodes.map((n) => n.id);
    return (
      <SortableContext
        key={`group-${parentId ?? "root"}`}
        items={ids}
        strategy={verticalListSortingStrategy}
      >
        {nodes.map((cat) => (
          <React.Fragment key={cat.id}>
            <SortableRow
              cat={cat}
              level={level}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleVisibility={onToggleVisibility}
              collapsed={collapsed}
              onToggleCollapse={handleToggleCollapse}
            />
            {Array.isArray(cat.subcategories) &&
              cat.subcategories.length > 0 &&
              !collapsed[cat.id] &&
              renderGroup(cat.subcategories, cat.id, level + 1)}
          </React.Fragment>
        ))}
      </SortableContext>
    );
  };

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    const parentA = idToParentId.get(active.id);
    const parentB = idToParentId.get(over.id);
    if (parentA !== parentB) return;

    const sibs = siblingsOf(tree, parentA);
    const ids = sibs.map((s) => s.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const nextIds = arrayMove(ids, oldIndex, newIndex);
    await applyLocalReorder(parentA ?? null, nextIds);
  };

  const currentEditPreviewSrc = imagePreview
    ? imagePreview
    : resolveImageSrc({ image: editingImage, imageUrl: editingImageUrl });

  return (
    <div className="p-6 bg-[#F5F0E6] min-h-screen">
      <h2 className="text-2xl font-bold mb-6 text-[#4B2E2B]">Категорії</h2>

      {/* ===== Форма ===== */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-[#D2B48C] mb-6 max-w-4xl mx-auto">
        <h3 className="text-lg font-semibold mb-4 text-[#4B2E2B]">
          {editingId ? "Редагування категорії" : "Створення категорії"}
        </h3>

        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={onFormChange}
            placeholder="Назва категорії (укр.)"
            required
            className="border p-2 rounded w-full"
          />

          <input
            type="text"
            name="name_en"
            value={form.name_en}
            onChange={onFormChange}
            placeholder="Name (English)"
            className="border p-2 rounded w-full"
          />

          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickImage}
              className="border p-2 rounded w-full"
            />
            {currentEditPreviewSrc && (
              <div className="mt-1">
                <div className="text-sm text-gray-600 mb-1">Попередній перегляд:</div>
                <img
                  src={currentEditPreviewSrc}
                  alt="preview"
                  className="h-24 w-24 object-cover rounded border"
                />
              </div>
            )}
          </div>

          <select
            name="parentId"
            value={form.parentId || ""}
            onChange={onFormChange}
            className="border p-2 rounded w-full"
          >
            <option value="">— Немає батьківської категорії —</option>
            {flatCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <div className="flex gap-4 mt-4 md:col-span-2">
            <button
              type="submit"
              className="bg-[#4B2E2B] text-white px-6 py-2 rounded hover:bg-[#3A2422]"
            >
              {editingId ? "Оновити" : "Створити"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={onCancelEdit}
                className="border px-6 py-2 rounded hover:bg-gray-100"
              >
                Скасувати
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ===== Таблиця ===== */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-[#D2B48C] max-w-6xl mx-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="w-full border-collapse">
            <thead className="bg-[#4B2E2B] text-white">
              <tr>
                <th className="p-3 text-left w-[70px]">Фото</th>
                <th className="p-3 text-left">Назва</th>
                <th className="p-3 text-left">Тип</th>
                <th className="p-3 text-left w-[220px]">Дії</th>
              </tr>
            </thead>
            <tbody>{useMemo(() => renderGroup(tree, null, 0), [tree, collapsed])}</tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
}


// // src/pages/CategoriesPage.jsx
// import React, { useEffect, useMemo, useRef, useState } from "react";
// import {
//   getCategories,
//   createCategory,
//   updateCategory,
//   deleteCategory,
//   reorderCategories,
// } from "../services/categoryService";

// import {
//   DndContext,
//   PointerSensor,
//   useSensor,
//   useSensors,
//   closestCenter,
// } from "@dnd-kit/core";
// import {
//   SortableContext,
//   useSortable,
//   arrayMove,
//   verticalListSortingStrategy,
// } from "@dnd-kit/sortable";
// import { CSS } from "@dnd-kit/utilities";

// // ===== ENV =====
// const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
// const CLOUD_NAME =
//   process.env.REACT_APP_CLOUDINARY_CLOUD ||
//   process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
//   "";

// // ===== Helpers =====
// function resolveImageSrc({ image, imageUrl }) {
//   if (typeof imageUrl === "string" && imageUrl.startsWith("http")) return imageUrl;
//   if (typeof image === "string" && image.startsWith("http")) return image;
//   if (
//     CLOUD_NAME &&
//     typeof image === "string" &&
//     image.trim() &&
//     !image.includes("..") &&
//     !image.includes("uploads/")
//   ) {
//     const publicId = image.replace(/^\/+/, "");
//     return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/q_auto,f_auto/${publicId}`;
//   }
//   if (typeof image === "string" && image.trim()) return `${API_BASE}/uploads/${image}`;
//   return null;
// }

// function flattenCategories(cats, prefix = "") {
//   return cats.flatMap((cat) => [
//     { id: cat.id, name: `${prefix}${cat.name}`, parentId: cat.parentId },
//     ...(cat.subcategories
//       ? flattenCategories(cat.subcategories, prefix + "--")
//       : []),
//   ]);
// }

// // Формуємо мапу id→parentId (щоб визначати «серед яких братів» відбувається dnd)
// function mapIdToParentId(tree) {
//   const map = new Map();
//   const walk = (nodes, parentId = null) => {
//     for (const n of nodes) {
//       map.set(n.id, parentId);
//       if (Array.isArray(n.subcategories) && n.subcategories.length) {
//         walk(n.subcategories, n.id);
//       }
//     }
//   };
//   walk(tree, null);
//   return map;
// }

// // Зібрати список «братів» для вказаного parentId
// function siblingsOf(tree, parentId) {
//   if (parentId == null) return tree;
//   const stack = [...tree];
//   while (stack.length) {
//     const node = stack.pop();
//     if (!node) continue;
//     if (node.id === parentId) return node.subcategories || [];
//     if (Array.isArray(node.subcategories)) stack.push(...node.subcategories);
//   }
//   return [];
// }

// // Замінити порядок «братів» для певного parentId
// function replaceSiblings(tree, parentId, nextSiblings) {
//   const clone = (n) => ({
//     ...n,
//     subcategories: Array.isArray(n.subcategories) ? n.subcategories.map(clone) : [],
//   });

//   if (parentId == null) return nextSiblings.map(clone);

//   const deep = tree.map(clone);
//   const dfs = (nodes) => {
//     for (const n of nodes) {
//       if (n.id === parentId) {
//         n.subcategories = nextSiblings.map(clone);
//         return true;
//       }
//       if (Array.isArray(n.subcategories) && dfs(n.subcategories)) return true;
//     }
//     return false;
//   };
//   dfs(deep);
//   return deep;
// }

// // ===== Sortable Row =====
// function SortableRow({ cat, level, onEdit, onDelete, collapsed, onToggle }) {
//   const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
//     useSortable({ id: cat.id });

//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//     background: isDragging ? "#FFF8E1" : undefined,
//   };

//   const src = resolveImageSrc({ image: cat.image, imageUrl: cat.imageUrl });

//   return (
//     <tr ref={setNodeRef} style={style} className="border-b hover:bg-[#F5F0E6] transition">
//       <td className="p-3 w-[70px]">
//         {src ? (
//           <img
//             src={src}
//             alt={cat.name}
//             className="h-16 w-16 object-cover rounded shadow-sm"
//           />
//         ) : (
//           <div className="h-16 w-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
//             No
//           </div>
//         )}
//       </td>

//       <td className="p-3 font-semibold text-[#4B2E2B]">
//         <div className="flex items-center gap-2">
//           <button
//             {...attributes}
//             {...listeners}
//             className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700 px-2"
//             title="Перетягнути"
//           >
//             ☰
//           </button>

//           <span style={{ paddingLeft: `${level * 16}px` }}>
//             {level > 0 && <span className="mr-1">↳</span>}
//             {cat.name}
//             {Array.isArray(cat.subcategories) && cat.subcategories.length > 0 && (
//               <button
//                 type="button"
//                 onClick={() => onToggle(cat.id)}
//                 className="ml-2 text-gray-500 hover:text-gray-700"
//                 title={collapsed[cat.id] ? "Розгорнути" : "Згорнути"}
//               >
//                 {collapsed[cat.id] ? "▶" : "▼"}
//               </button>
//             )}
//           </span>
//         </div>
//         {/* Англійська назва під основною, якщо є */}
//         {cat.name_en ? (
//           <div className="text-xs text-gray-500 ml-10" style={{ paddingLeft: `${level * 16}px` }}>
//             EN: {cat.name_en}
//           </div>
//         ) : null}
//       </td>

//       <td className="p-3 text-gray-600 italic">
//         {cat.parentId ? "Підкатегорія" : "Головна"}
//       </td>

//       <td className="p-3 flex gap-2">
//         <button
//           onClick={() => onEdit(cat)}
//           className="bg-[#A47551] text-white px-3 py-1 rounded hover:bg-[#8B5E3C]"
//         >
//           Edit
//         </button>
//         <button
//           onClick={() => onDelete(cat.id)}
//           className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
//         >
//           Delete
//         </button>
//       </td>
//     </tr>
//   );
// }

// // ===== Page =====
// export default function CategoriesPage() {
//   const [tree, setTree] = useState([]);
//   const [flatCategories, setFlatCategories] = useState([]);
//   const [collapsed, setCollapsed] = useState({});

//   // форму розширено: додаємо name_en
//   const [form, setForm] = useState({ name: "", name_en: "", parentId: "" });
//   const [image, setImage] = useState(null);
//   const [imagePreview, setImagePreview] = useState(null);
//   const [editingId, setEditingId] = useState(null);
//   const [editingImage, setEditingImage] = useState(null);
//   const [editingImageUrl, setEditingImageUrl] = useState(null);

//   // id → parentId
//   const idToParentId = useMemo(() => mapIdToParentId(tree), [tree]);

//   const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
//   const fileInputRef = useRef(null);

//   // ===== Load =====
//   const load = async () => {
//     const data = await getCategories();
//     setTree(data);
//     const collapsedMap = {};
//     data.forEach((cat) => {
//       if (!cat.parentId && cat.subcategories?.length) collapsedMap[cat.id] = true;
//     });
//     setCollapsed(collapsedMap);
//     setFlatCategories(flattenCategories(data));
//   };

//   useEffect(() => {
//     load().catch(console.error);
//   }, []);

//   const handleToggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));
//   const onFormChange = (e) =>
//     setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

//   const onPickImage = (e) => setImage(e.target.files?.[0] || null);
//   useEffect(() => {
//     if (!image) return setImagePreview(null);
//     const url = URL.createObjectURL(image);
//     setImagePreview(url);
//     return () => URL.revokeObjectURL(url);
//   }, [image]);

//   const clearFile = () => fileInputRef.current && (fileInputRef.current.value = "");
//   const resetForm = () => {
//     setForm({ name: "", name_en: "", parentId: "" });
//     setImage(null);
//     setImagePreview(null);
//     setEditingId(null);
//     setEditingImage(null);
//     setEditingImageUrl(null);
//     clearFile();
//   };

//   // ===== CRUD =====
//   const onSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const fd = new FormData();
//       fd.append("name", form.name);
//       if (form.name_en) fd.append("name_en", form.name_en); // 🟢 додаємо EN поле
//       fd.append("parentId", form.parentId || "");
//       if (image) fd.append("image", image);

//       if (editingId) await updateCategory(editingId, fd);
//       else await createCategory(fd);
//       resetForm();
//       await load();
//     } catch (err) {
//       console.error("Помилка збереження категорії:", err);
//       alert("Не вдалося зберегти категорію");
//     }
//   };

//   const onDelete = async (id) => {
//     if (!window.confirm("Видалити категорію?")) return;
//     try {
//       await deleteCategory(id);
//       await load();
//     } catch (err) {
//       console.error("Помилка видалення категорії:", err);
//       alert("Не вдалося видалити категорію");
//     }
//   };

//   const onEdit = (cat) => {
//     setEditingId(cat.id);
//     setForm({
//       name: cat.name || "",
//       name_en: cat.name_en || "",
//       parentId: cat.parentId || "",
//     });
//     setEditingImage(cat.image || null);
//     setEditingImageUrl(cat.imageUrl || null);
//     setImage(null);
//     setImagePreview(null);
//     clearFile();
//   };

//   const onCancelEdit = () => resetForm();

//   const applyLocalReorder = async (parentId, newOrderIds) => {
//     const sibs = siblingsOf(tree, parentId);
//     const next = newOrderIds.map((id) => sibs.find((s) => s.id === id)).filter(Boolean);
//     setTree((prev) => replaceSiblings(prev, parentId, next));
//     try {
//       await reorderCategories(parentId ?? null, newOrderIds);
//     } catch (e) {
//       console.error("Помилка збереження порядку:", e);
//       await load();
//     }
//   };

//   // ===== Drag & Drop =====
//   // Рендеримо таблицю з ОДНИМ <tbody>. Для кожної «групи братів» — власний SortableContext у Fragment.
//   const renderGroup = (nodes, parentId = null, level = 0) => {
//     const ids = nodes.map((n) => n.id);
//     return (
//       <SortableContext key={`group-${parentId ?? "root"}`} items={ids} strategy={verticalListSortingStrategy}>
//         {nodes.map((cat) => (
//           <React.Fragment key={cat.id}>
//             <SortableRow
//               cat={cat}
//               level={level}
//               onEdit={onEdit}
//               onDelete={onDelete}
//               collapsed={collapsed}
//               onToggle={handleToggle}
//             />
//             {Array.isArray(cat.subcategories) &&
//               cat.subcategories.length > 0 &&
//               !collapsed[cat.id] &&
//               renderGroup(cat.subcategories, cat.id, level + 1)}
//           </React.Fragment>
//         ))}
//       </SortableContext>
//     );
//   };

//   const allRenderedRows = useMemo(() => renderGroup(tree, null, 0), [tree, collapsed]);

//   const handleDragEnd = async (e) => {
//     const { active, over } = e;
//     if (!over || active.id === over.id) return;

//     // Дозволяємо сортування лише серед братів (той самий parentId)
//     const parentA = idToParentId.get(active.id);
//     const parentB = idToParentId.get(over.id);
//     if (parentA !== parentB) return;

//     const sibs = siblingsOf(tree, parentA);
//     const ids = sibs.map((s) => s.id);
//     const oldIndex = ids.indexOf(active.id);
//     const newIndex = ids.indexOf(over.id);
//     if (oldIndex === -1 || newIndex === -1) return;

//     const nextIds = arrayMove(ids, oldIndex, newIndex);
//     await applyLocalReorder(parentA ?? null, nextIds);
//   };

//   const currentEditPreviewSrc = imagePreview
//     ? imagePreview
//     : resolveImageSrc({ image: editingImage, imageUrl: editingImageUrl });

//   return (
//     <div className="p-6 bg-[#F5F0E6] min-h-screen">
//       <h2 className="text-2xl font-bold mb-6 text-[#4B2E2B]">Категорії</h2>

//       {/* ===== Форма ===== */}
//       <div className="bg-white p-6 rounded-lg shadow-md border border-[#D2B48C] mb-6 max-w-4xl mx-auto">
//         <h3 className="text-lg font-semibold mb-4 text-[#4B2E2B]">
//           {editingId ? "Редагування категорії" : "Створення категорії"}
//         </h3>

//         <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
//           {/* Назва UA */}
//           <input
//             type="text"
//             name="name"
//             value={form.name}
//             onChange={onFormChange}
//             placeholder="Назва категорії (укр.)"
//             required
//             className="border p-2 rounded w-full"
//           />

//           {/* Назва EN */}
//           <input
//             type="text"
//             name="name_en"
//             value={form.name_en}
//             onChange={onFormChange}
//             placeholder="Name (English)"
//             className="border p-2 rounded w-full"
//           />

//           {/* Зображення */}
//           <div className="flex flex-col gap-2">
//             <input
//               ref={fileInputRef}
//               type="file"
//               accept="image/*"
//               onChange={onPickImage}
//               className="border p-2 rounded w-full"
//             />
//             {currentEditPreviewSrc && (
//               <div className="mt-1">
//                 <div className="text-sm text-gray-600 mb-1">Попередній перегляд:</div>
//                 <img
//                   src={currentEditPreviewSrc}
//                   alt="preview"
//                   className="h-24 w-24 object-cover rounded border"
//                 />
//               </div>
//             )}
//           </div>

//           {/* Parent */}
//           <select
//             name="parentId"
//             value={form.parentId || ""}
//             onChange={onFormChange}
//             className="border p-2 rounded w-full"
//           >
//             <option value="">— Немає батьківської категорії —</option>
//             {flatCategories.map((c) => (
//               <option key={c.id} value={c.id}>
//                 {c.name}
//               </option>
//             ))}
//           </select>

//           <div className="flex gap-4 mt-4 md:col-span-2">
//             <button type="submit" className="bg-[#4B2E2B] text-white px-6 py-2 rounded hover:bg-[#3A2422]">
//               {editingId ? "Оновити" : "Створити"}
//             </button>
//             {editingId && (
//               <button
//                 type="button"
//                 onClick={onCancelEdit}
//                 className="border px-6 py-2 rounded hover:bg-gray-100"
//               >
//                 Скасувати
//               </button>
//             )}
//           </div>
//         </form>
//       </div>

//       {/* ===== Таблиця ===== */}
//       <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-[#D2B48C] max-w-6xl mx-auto">
//         <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
//           <table className="w-full border-collapse">
//             <thead className="bg-[#4B2E2B] text-white">
//               <tr>
//                 <th className="p-3 text-left w-[70px]">Фото</th>
//                 <th className="p-3 text-left">Назва</th>
//                 <th className="p-3 text-left">Тип</th>
//                 <th className="p-3 text-left w-[150px]">Дії</th>
//               </tr>
//             </thead>
//             {/* Один-єдиний tbody на всю таблицю */}
//             <tbody>
//               {useMemo(() => renderGroup(tree, null, 0), [tree, collapsed])}
//             </tbody>
//           </table>
//         </DndContext>
//       </div>
//     </div>
//   );
// }




